import type {
  NFLGameInputs,
  NFLPlayerInputs,
  NFLTeamInputs,
  RequestSimulationRunResponse,
  SimulationRun
} from '@library/types/simulations';
import type { NavigationSteps, StepperStep } from '@library/types/utils';
import {
  determineInitialSimulationStep,
  determineStepStatus
} from '@bo-utils/simulations';
import { useEffect, useState } from 'react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueTeamPlayer } from '@library/types/player';
import type { SerializedError } from '@reduxjs/toolkit';
import type { Updater } from 'use-immer';
import { useImmer } from 'use-immer';
import { useNavigate } from 'react-router-dom';
import { usePublishSimulation } from '@bo-hooks/simulations/usePublishSimulation';

export type OverrideValidationError = {
  playerId?: string;
  field: keyof NFLPlayerInputs | keyof NFLGameInputs | keyof NFLTeamInputs;
};

export function useSimulationDetailSteps({
  gameId,
  handleRequestSimulationRun,
  isNewSimulation,
  leagueId,
  players,
  proposedProjections,
  simulation,
  simulationId
}: {
  gameId: string;
  handleRequestSimulationRun: () => Promise<
    | {
        data: RequestSimulationRunResponse;
      }
    | {
        error: FetchBaseQueryError | SerializedError;
      }
  >;
  isNewSimulation: boolean;
  leagueId: string;
  players: Array<LeagueTeamPlayer>;
  proposedProjections: Array<GamePlayerProjectionTrading>;
  simulation?: SimulationRun;
  simulationId: string;
}): {
  navigationSteps: NavigationSteps;
  overrideValidationErrors: Array<OverrideValidationError>;
  publishSimulationStatus: {
    isPublishSimulationError: boolean;
    isPublishSimulationLoading: boolean;
    isPublishSimulationSuccess: boolean;
  };
  setOverrideValidationErrors: Updater<Array<OverrideValidationError>>;
  step?: number;
  steps: Array<StepperStep>;
} {
  const navigate = useNavigate();

  const [step, setStep] = useState<number>();

  const [overrideValidationErrors, setOverrideValidationErrors] = useImmer<
    Array<{
      playerId?: string;
      field: keyof NFLPlayerInputs | keyof NFLGameInputs | keyof NFLTeamInputs;
    }>
  >([]);

  useEffect(() => {
    if (!step) {
      if (!isNewSimulation && simulation) {
        setStep(determineInitialSimulationStep(simulation));
      } else {
        setStep(1);
      }
    }
  }, [simulation, step, isNewSimulation]);

  const {
    handlePublishSimulation,
    isPublishSimulationError,
    isPublishSimulationLoading,
    isPublishSimulationSuccess
  } = usePublishSimulation();

  const publishSimulationStatus = {
    isPublishSimulationError,
    isPublishSimulationLoading,
    isPublishSimulationSuccess
  };

  const steps: Array<StepperStep> = [
    {
      name: 'Build',
      idx: 1,
      status: determineStepStatus({ callingStep: 1, currentStep: step })
    },
    {
      name: 'Results',
      idx: 2,
      status: determineStepStatus({ callingStep: 2, currentStep: step })
    },
    {
      name: 'Finalize',
      idx: 3,
      status: determineStepStatus({ callingStep: 3, currentStep: step })
    },
    {
      name: 'Publish',
      idx: 4,
      status: determineStepStatus({ callingStep: 4, currentStep: step })
    }
  ];

  const determineNextDisabled = () => {
    if (step === 1) {
      if (
        !simulation ||
        (simulation &&
          simulation.data.attributes.simulation_run_type !== 'CANDIDATE')
      ) {
        return true;
      }
    }

    if (step === 2) {
      if (
        simulation &&
        simulation.data.attributes.simulation_run_status !==
          'SIMULATION_COMPLETED' &&
        simulation.data.attributes.simulation_run_type !== 'CANDIDATE'
      )
        return true;
    }

    if (step === 3) {
      return !proposedProjections.length;
    }

    if (step && step > steps.length - 1) {
      return true;
    }

    return false;
  };

  const determineBackDisabled = () => {
    if (
      step &&
      step > steps.length - 1 &&
      (isPublishSimulationLoading || isPublishSimulationSuccess)
    ) {
      return true;
    }

    return false;
  };

  const handleClickBack = () => {
    if (step === 1) {
      if (isNewSimulation || !gameId || !leagueId) {
        navigate('/simulations');
        return;
      }

      navigate(`/simulations/${leagueId}/${gameId}`);
      return;
    }

    setStep((newStep) => newStep && newStep - 1);
  };

  const handleClickNext = async () => {
    if (step === 1 && simulationId === 'NEW') {
      await handleRequestSimulationRun().catch(() => {});
    }

    if (step === 3 && simulationId) {
      handlePublishSimulation({
        gameId,
        leagueId,
        players,
        pricingStrategy: 'SIMULATION_BASED_PRICING',
        modifiedProjections: proposedProjections,
        simulationId
      }).catch(() => {});
    }

    setStep((newStep) => newStep && newStep + 1);
  };

  const navigationSteps = {
    currentStep: step ?? 1,
    back: {
      label: 'Back',
      onClick: handleClickBack,
      disabled: determineBackDisabled()
    },
    next: {
      label: 'Next',
      onClick: handleClickNext,
      disabled: determineNextDisabled()
    }
  };

  return {
    navigationSteps,
    overrideValidationErrors,
    publishSimulationStatus,
    setOverrideValidationErrors,
    step,
    steps
  };
}
